import { Modal, Form, Select, Button, Typography, Spin, Alert, Descriptions, Tag } from 'antd';

import { IInvestimentoAtivacao, IContaDaFonteInvestimento } from '../../../../../../../../InvestimentosACadastrar/interfaces';

import { useAlterarContaModal } from './useAlterarContaModal';
import { convertMoedaBRDoJSComExtamente2CasasDecimais } from '../../../../../../../../../../services/Conversores';
import { removeAcento } from '../../../../../../../../../../utils/RemoveAcentos';

const { Text } = Typography;

interface IProps {
    visible: boolean;
    investimento: IInvestimentoAtivacao | null;
    onClose: () => void;
    onSuccess: (investimentoId: number, novaContaId: number) => void;
}

export default function AlterarContaModal({
    visible,
    investimento,
    onClose,
    onSuccess,
}: IProps) {
    const {
        form,
        loadingFontes,
        submitting,
        fontes,
        fonteSelecionadaNumero,
        contasDisponiveis,
        handleChangeFonte,
        totalContas,
        handleFinish,
    } = useAlterarContaModal({
        visible,
        investimento,
        onClose,
        onSuccess,
    });

    return (
        <Modal
            title="Alterar conta do investimento"
            open={visible}
            onCancel={onClose}
            destroyOnClose
            footer={null}
            width={650}
        >
            {investimento && (
                <div style={{ marginBottom: 20 }}>
                    <Descriptions size="small" bordered column={1}>
                        <Descriptions.Item label="Investimento">
                            <Text strong>{investimento.nome}</Text>
                        </Descriptions.Item>
                        {investimento.descricao && (
                            <Descriptions.Item label="Descrição">
                                {investimento.descricao}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Origem">
                            <Tag color={investimento.origem === 'OPEN_FINANCE' ? 'blue' : 'cyan'}>
                                {investimento.origem === 'OPEN_FINANCE' ? 'Open Finance' : 'Manual'}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Conta atual">
                            {investimento.conta ? (
                                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                                    <span style={{ fontWeight: 500 }}>
                                        Ag: {investimento.conta.agencia || '-'} | Conta: {investimento.conta.numero || '-'}
                                    </span>
                                    {investimento.conta.descricao && (
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {investimento.conta.descricao}
                                        </Text>
                                    )}
                                </div>
                            ) : (
                                '--'
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Banco atual">
                            {investimento.conta?.banco?.nome || '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Órgão vinculado atual">
                            {investimento.conta?.orgaoVinculado?.nome || '-'}
                        </Descriptions.Item>
                        {investimento.valor !== null && investimento.valor !== undefined && (
                            <Descriptions.Item label="Último valor">
                                {`R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(investimento.valor, true)}`}
                                {investimento.mes && investimento.ano ? ` (${String(investimento.mes).padStart(2, '0')}/${investimento.ano})` : ''}
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                </div>
            )}

            <Spin spinning={loadingFontes}>
                {!loadingFontes && fontes.length > 0 && totalContas > 0 && (
                    <Alert
                        type="info"
                        showIcon
                        message="A conta bancária atual deste investimento não é exibida na lista de opções, pois são apresentadas apenas as outras contas disponíveis para vínculo."
                        style={{ marginBottom: 16 }}
                    />
                )}

                {!loadingFontes && fontes.length === 0 && (
                    <Alert
                        type="info"
                        showIcon
                        message="Este investimento não possui outras contas disponíveis para vínculo."
                        style={{ marginBottom: 16 }}
                    />
                )}

                {!loadingFontes && fontes.length > 0 && totalContas === 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        message="As fontes disponíveis não possuem contas vinculadas."
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleFinish}
                >
                    <Form.Item
                        name="fonteNumero"
                        label="Nova fonte"
                        rules={[{ required: true, message: 'Por favor, selecione a fonte!' }]}
                    >
                        <Select
                            placeholder="Selecione a fonte"
                            showSearch
                            allowClear
                            optionLabelProp="title"
                            onChange={handleChangeFonte}
                            filterOption={(input: any, option: any) => {
                                let textDigit = removeAcento(input);
                                let searchableText = removeAcento(option?.searchValue || '');
                                return searchableText.indexOf(textDigit) >= 0;
                            }}
                            options={fontes.map((e) => ({
                                value: e.numero,
                                title: `${e.numero} - ${e.descricao}`,
                                searchValue: `${e.numero} - ${e.descricao} ${e.categoria?.nome || ''}`,
                                label: (
                                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, padding: '2px 0' }}>
                                        <span style={{ fontWeight: 500 }}>{e.numero} - {e.descricao}</span>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                            {e.categoria?.nome}
                                        </Typography.Text>
                                    </div>
                                ),
                            }))}
                            disabled={loadingFontes || fontes.length === 0}
                        />
                    </Form.Item>

                    <Form.Item
                        name="contaId"
                        label="Nova conta bancária"
                        rules={[{ required: true, message: 'Por favor, selecione a nova conta!' }]}
                    >
                        <Select
                            placeholder={
                                !fonteSelecionadaNumero
                                    ? 'Selecione primeiro uma fonte'
                                    : contasDisponiveis.length === 0
                                        ? 'Nenhuma conta disponível vinculada a esta fonte'
                                        : 'Selecione a nova conta'
                            }
                            showSearch
                            allowClear
                            optionLabelProp="title"
                            filterOption={(input: any, option: any) => {
                                let textDigit = removeAcento(input);
                                let searchableText = removeAcento(option?.searchValue || '');
                                return searchableText.indexOf(textDigit) >= 0;
                            }}
                            options={contasDisponiveis.map((conta: IContaDaFonteInvestimento) => ({
                                value: conta.id,
                                title: `Agência: ${conta.agencia} | Conta: ${conta.numero}${conta.descricao ? ` - ${conta.descricao}` : ''}`,
                                searchValue: `${conta.agencia} ${conta.numero} ${conta.descricao || ''}`,
                                label: (
                                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3, padding: '2px 0' }}>
                                        <span style={{ fontWeight: 500 }}>Agência: {conta.agencia} | Conta: {conta.numero}</span>
                                        {conta.descricao && (
                                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                {conta.descricao}
                                            </Typography.Text>
                                        )}
                                    </div>
                                ),
                            }))}
                            disabled={!fonteSelecionadaNumero || contasDisponiveis.length === 0}
                        />
                    </Form.Item>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24, gap: 8 }}>
                        <Button onClick={onClose} disabled={submitting}>
                            Cancelar
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            className="btn_success_vlhbegoawrgbvr"
                            loading={submitting}
                            disabled={loadingFontes || !fonteSelecionadaNumero || contasDisponiveis.length === 0}
                        >
                            Salvar alteração
                        </Button>
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
}
